import React, { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Button, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import BusinessServiceFeesCreateEditModal from '~/components/LeBusinessTraveller/components/serviceFees/BusinessServiceFeesCreateEditModal';
import BusinessServiceFeesResetModal from '~/components/LeBusinessTraveller/components/serviceFees/BusinessServiceFeesResetModal';
import ServiceFeeGridActions from '~/components/LeBusinessTraveller/components/serviceFees/ServiceFeeGridActions';

import { serviceFeeQueryKeys, useGetServiceFees } from '~/hooks/apis/business-traveller/useGetServiceFees';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';
import useToggleState from '~/hooks/useToggleState';

interface Params {
  businessId: string;
}

export default function ServiceFeeTab() {
  const { businessId } = useParams<keyof Params & string>();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const { isToggled: isCreateDialogOpen, toggleOn: openCreateDialog, toggleOff: closeCreateDialog } = useToggleState();
  const { isToggled: isResetDialogOpen, toggleOn: openResetDialog, toggleOff: closeResetDialog } = useToggleState();

  const { data, isLoading, error } = useGetServiceFees(businessId, paginationModel);

  useEffect(() => {
    if (error) {
      const message = error instanceof Error ? error.message : JSON.stringify(error, null, 2);
      enqueueSnackbar(`Error fetching service fees [${message}]`, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const serviceFees = data?.result ?? [];
  const serviceFeeTotal = data?.meta?.total ?? 0;

  const refetchServiceFees = () => {
    queryClient.invalidateQueries({ queryKey: serviceFeeQueryKeys.all });
  };

  const columns: Array<GridColDef> = [
    { field: 'source', headerName: 'Source', flex: 1 },
    { field: 'type', headerName: 'Type', flex: 1 },
    { field: 'destination', headerName: 'Destination', flex: 1 },
    { field: 'product', headerName: 'Product', flex: 1 },
    { field: 'amount', headerName: 'Amount', flex: 1 },
    { field: 'conciergeAmount', headerName: 'Concierge Amount', flex: 1 },
    {
      field: 'context-menu',
      type: 'actions',
      headerName: '',
      sortable: false,
      getActions: (params) => [
        <ServiceFeeGridActions serviceFee={params.row} onSuccess={refetchServiceFees} key={params.row.id} />,
      ],
    },
  ];

  return (
    <Stack gap={2}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Stack direction="row" justifyContent="end" alignItems="center" gap={2}>
            <Button variant="contained" color="primary" onClick={openCreateDialog}>
              Create Service Fee
            </Button>
            <Button variant="contained" color="primary" onClick={openResetDialog}>
              {serviceFees.length > 0 ? 'Reset To Default' : 'Add Default Fees'}
            </Button>
            <BusinessServiceFeesCreateEditModal
              businessId={businessId}
              isOpen={isCreateDialogOpen}
              onClose={closeCreateDialog}
              onSuccess={refetchServiceFees}
              mode="create"
            />
            <BusinessServiceFeesResetModal
              businessId={businessId}
              serviceFeeCount={serviceFees.length}
              isOpen={isResetDialogOpen}
              onClose={closeResetDialog}
              onSuccess={refetchServiceFees}
            />
          </Stack>
        </Grid>
        <Grid size={12}>
          <DataGrid
            columns={columns}
            loading={isLoading}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            paginationModel={paginationModel}
            rows={serviceFees}
            rowCount={serviceFeeTotal}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
